import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  InputAdornment,
  Link,
  AppBar,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import SearchIcon from "@mui/icons-material/Search";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PageDataContext, PageDataContextType } from "../../utils/common/PageDataContext.tsx";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import { useSelector } from "react-redux";
import { externalgetApi } from "../../services/externalApis.tsx";
import { ITEM_API_01 } from "../../api/ApiModel.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { MSG_ITEMS_LOAD_FAILED } from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface UserParams {
  COMPCODE?: string;
  STAFFMAPLOCODE?: string;
  USERNAME?: string;
  [key: string]: any;
}

interface UserState {
  USER_PARAM?: UserParams;
  [key: string]: any;
}

interface RootState {
  user: UserState;
}

interface OriginalItem {
  itemcode?: string | number;
  itemname?: string;
  variantdata?: string;
  endate?: string;
  uptype?: string;
  sts?: string;
  modelname?: string;
  brandcode?: string | number;
  maincatcode?: string | number;
  catcode?: string | number;
  subcatcode?: string | number;
  genericcode?: string | number;
  packagecode?: string | number;
  measuringunitcode?: string | number;
  packageunits?: number;
  descrip?: string;
  productimgs?: string;
  docpathjson?: string;
  maincatname?: string;
  catname?: string;
  subcatname?: string;
  genericname?: string;
  brandname?: string;
  [key: string]: any;
}

interface TransformedItem {
  id: string | number;
  itemcode: string | number;
  itemname: string;
  varianttype: string;
  createddate: string;
  uptype: string;
  status: string;
  modelname: string;
  brandcode: string | number;
  maincatcode: string | number;
  catcode: string | number;
  subcatcode: string | number;
  genericcode: string | number;
  packagecode: string | number;
  measuringunitcode: string | number;
  packageunits: number;
  descrip: string;
  originalData: OriginalItem;
}

interface VariantItem {
  variantname?: string;
  variantName?: string;
  variantvalue?: string;
  variantValue?: string;
}

interface GroupedVariant {
  [key: string]: string[];
}

interface GridColumn {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  minWidth?: number;
  headerAlign?: "center" | "left" | "right";
  align?: "center" | "left" | "right";
  renderCell?: (params: any) => React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

// Parse common images from raw API payload
const parseCommonImagesFromItem = (item: OriginalItem): any[] => {
  const raw = item.productimgs || item.docpathjson;
  if (!raw) return [];

  try {
    let jsonString: string = raw;
    if (typeof raw === "string") {
      jsonString = raw.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
      if (jsonString.startsWith('"') && jsonString.endsWith('"')) {
        jsonString = jsonString.slice(1, -1);
      }
    }

    let parsed: any[] = [];
    try {
      parsed = JSON.parse(jsonString);
    } catch {
      try {
        parsed = JSON.parse(raw);
      } catch {
        if (typeof raw === "string" && raw.includes("[")) {
          const start = raw.indexOf("[");
          const end = raw.lastIndexOf("]") + 1;
          if (start >= 0 && end > start) {
            parsed = JSON.parse(raw.substring(start, end));
          }
        }
      }
    }

    if (!Array.isArray(parsed)) parsed = [parsed];

    return parsed
      .filter(Boolean)
      .map((img: any, idx: number) => {
        let imageUrl = "";
        if (img.FILEPATH && (img.FILEPATH.startsWith("http") || img.FILEPATH.startsWith("data:"))) {
          imageUrl = img.FILEPATH;
        } else if (img.bytarr) {
          imageUrl = `data:image/jpeg;base64,${img.bytarr}`;
        } else if (img.filepath || img.data) {
          imageUrl = img.filepath || img.data;
        }

        if (!imageUrl) return null;

        const rawIsDefault = img.ISDEFAULT ?? img.isdefault ?? img.IsDefault ?? false;
        const isDefault =
          String(rawIsDefault).toUpperCase() === "Y" ||
          rawIsDefault === 1 ||
          rawIsDefault === true;

        return {
          id: Date.now() + idx,
          data: imageUrl,
          name: img.filename || img.FILEPATH?.split("/").pop() || `image_${idx}.jpg`,
          size: 0,
          type: "image/jpeg",
          filename: img.filename || img.FILEPATH?.split("/").pop() || `image_${idx}.jpg`,
          base64Data: img.bytarr || "",
          filepath: img.FILEPATH || img.filepath || "",
          filepathUrl: imageUrl,
          doctypecode: img.DOCTYPECODE || "IMG",
          doctypeothername: img.DOCTYPEOTHERNAME || "Item Image",
          remark: img.REMARK || img.remark || "",
          variantIdentifier: "Common Image",
          rawData: img,
          isDefault,
          isExisting: true,
        };
      })
      .filter(Boolean);
  } catch (e) {
    console.error("Failed to parse common images:", e);
    return [];
  }
};

const AddVariantPage: React.FC = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = useContext(PageDataContext) as PageDataContextType;

  const [data, setData] = useState<TransformedItem[]>([]);
  const [filteredRows, setFilteredRows] = useState<TransformedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning" | "info">("success");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const SYS_USER = useSelector((state: RootState) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE } = SYS_USER.USER_PARAM || {};

  const goBackToView = (): void => navigate("/vCreateItem");

  const formatVariantData = (variantData: string | undefined | null): string => {
    if (!variantData || variantData === "" || variantData === "None") {
      return "None";
    }

    try {
      let parsed: VariantItem[] = [];
      const str = typeof variantData === "string" ? variantData.trim() : String(variantData);

      if (str.startsWith("[")) {
        try {
          let cleaned = str.replace(/\\"/g, '"');
          if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
            cleaned = cleaned.slice(1, -1);
          }
          parsed = JSON.parse(cleaned);
        } catch (e) {
          parsed = JSON.parse(str);
        }
      } else {
        const entries = str.split(";").map((v) => v.trim()).filter(Boolean);
        parsed = entries
          .map((entry) => {
            const [type, valuesStr] = entry.split(":").map((s) => s.trim());
            if (!type || !valuesStr) return null;
            const values = valuesStr.split(",").map((v) => v.trim()).filter(Boolean);
            return values.map((val) => ({ variantname: type, variantvalue: val }));
          })
          .flat()
          .filter(Boolean) as VariantItem[];
      }

      if (!Array.isArray(parsed) || parsed.length === 0) return "None";

      const grouped: GroupedVariant = {};
      parsed.forEach((v: VariantItem) => {
        const name = (v.variantname || v.variantName || "").trim();
        const val = (v.variantvalue || v.variantValue || "").trim();
        if (!name) return;
        if (!grouped[name]) grouped[name] = [];
        if (val && !grouped[name].includes(val)) grouped[name].push(val);
      });

      return (
        Object.entries(grouped)
          .map(([name, values]) => `${name}: ${values.join(", ")}`)
          .join(" | ") || "None"
      );
    } catch (error) {
      return variantData || "None";
    }
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusText = (statusCode: string | undefined): string => {
    switch (statusCode) {
      case CON_STATUS_ACTIVE:
        return "Active";
      case "I":
        return "Inactive";
      case "D":
        return "Deleted";
      default:
        return "Active";
    }
  };

  const sortNewestFirst = (rows: TransformedItem[]): TransformedItem[] => {
    return [...rows].sort((a, b) => {
      const dateA = a.originalData?.endate ? new Date(a.originalData.endate).getTime() : 0;
      const dateB = b.originalData?.endate ? new Date(b.originalData.endate).getTime() : 0;
      if (dateB !== dateA) return dateB - dateA;
      const codeA = Number(a.itemcode) || 0;
      const codeB = Number(b.itemcode) || 0;
      return codeB - codeA;
    });
  };

  const fetchItems = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      };

      const response = await externalgetApi(ITEM_API_01, "GET", params);

      let itemsList: OriginalItem[] = [];
      if (response && Array.isArray(response)) {
        itemsList = response;
      } else if (response && typeof response === "object" && !Array.isArray(response)) {
        itemsList = Object.values(response);
      }

      if (itemsList.length > 0) {
        const transformedData: TransformedItem[] = itemsList.map((item, index) => ({
          id: item.itemcode || index + 1,
          itemcode: item.itemcode || "",
          itemname: item.itemname || "",
          varianttype: formatVariantData(item.variantdata),
          createddate: formatDate(item.endate),
          uptype: item.uptype || "",
          status: getStatusText(item.sts),
          modelname: item.modelname || "",
          brandcode: item.brandcode || "",
          maincatcode: item.maincatcode || "",
          catcode: item.catcode || "",
          subcatcode: item.subcatcode || "",
          genericcode: item.genericcode || "",
          packagecode: item.packagecode || "",
          measuringunitcode: item.measuringunitcode || "",
          packageunits: item.packageunits || 0,
          descrip: item.descrip || "",
          originalData: item,
        }));

        const sortedData = sortNewestFirst(transformedData);
        setData(sortedData);
        setFilteredRows(sortedData);
      } else {
        setData([]);
        setFilteredRows([]);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
      setAlertMessage(MSG_ITEMS_LOAD_FAILED);
      setAlertType("error");
      setAlertOpen(true);
      setData([]);
      setFilteredRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    setSearchQuery(value ?? "");

    if (!(value && String(value).trim())) {
      setFilteredRows(data);
      return;
    }

    const q = String(value).toLowerCase().trim();
    const safeStr = (val: any): string => (val != null ? String(val).toLowerCase() : "");

    const filtered = data.filter((row) => {
      const o = row.originalData || {};
      return (
        safeStr(row.itemcode).includes(q) ||
        safeStr(row.itemname).includes(q) ||
        safeStr(o.maincatname).includes(q) ||
        safeStr(o.catname).includes(q) ||
        safeStr(o.subcatname).includes(q) ||
        safeStr(o.genericname).includes(q) ||
        safeStr(o.brandname).includes(q) ||
        safeStr(row.status).includes(q) ||
        safeStr(row.createddate).includes(q)
      );
    });
    setFilteredRows(sortNewestFirst(filtered || []));
  };

  const handleSelectItem = (id: string | number): void => {
    const row = data.find((r) => r.itemcode === id);
    if (!row) return;

    const original = row.originalData || {};
    
    // Parse common images directly from the selected row
    const parsedCommonImages = parseCommonImagesFromItem(original);

    console.log("Navigating with common images:", parsedCommonImages);

    navigate("/vCreateVariant", {
      state: {
        selectedItem: original,
        itemcode: row.itemcode,
        itemname: row.itemname,
        packagecode: original.packagecode,
        measuringunitcode: original.measuringunitcode,
        descrip: row.descrip,
        // Pass the parsed images and force common mode
        commonImages: parsedCommonImages,
        imageType: "common",
        isEditMode: false,
        fromCreateVariant: true,
      },
    });
  };

  useEffect(() => {
    setPageData({ title: "Item Management" });
    if (COMPCODE && STAFFMAPLOCODE) {
      fetchItems();
    }
  }, [COMPCODE, STAFFMAPLOCODE]);

  const menuItems: MenuItem[] = [
    {
      text: "Select",
      icon: <BorderColorOutlinedIcon />,
      onClick: (id: string | number) => handleSelectItem(id),
    },
  ];

  const itemGridData: { name: string; columns: GridColumn[] } = {
    name: "Item List",
    columns: [
      {
        field: "itemcode",
        headerName: "Item Code",
        width: 120,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "itemname",
        headerName: "Item Name",
        width: 150,
        flex: 1,
        minWidth: 150,
      },
      {
        field: "varianttype",
        headerName: "Variant Type",
        width: 200,
        flex: 1,
        minWidth: 200,
        renderCell: (params: any) => {
          const fullText = params.value || "None";
          const displayText = fullText.length > 30 ? `${fullText.substring(0, 30)}...` : fullText;
          return (
            <Tooltip title={fullText} arrow placement="top">
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {displayText}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: "createddate",
        headerName: "Created Date",
        width: 120,
        headerAlign: "center",
        align: "center",
      },
    ],
  };

  const preparedRows = filteredRows.map((row) => ({ ...row }));

  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title="Back to Item Create" arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon sx={styles.btn_breadcrumb_back_icon} />
          </IconButton>
        </Tooltip>

        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs separator="›" aria-label="breadcrumb" sx={styles.breadcrumb}>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            Home
          </Link>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            Item Management
          </Link>
          <Link underline="hover" sx={styles.breadcrumb_link_page} aria-current="page">
            Add Variant
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid
            container
            spacing={2}
            sx={{
              ...styles.appbar_grid_container,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Grid>
              <Typography sx={styles.appbar_title}>Add Variant</Typography>
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Autocomplete
                freeSolo
                disableClearable
                fullWidth
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={styles.ipt_appbar_search_auto}
                options={data?.map((row) => row.itemname) || []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by..."
                    size="small"
                    sx={styles.ipt_appbar_search}
                    InputProps={{
                      ...params.InputProps,
                      type: "search",
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton sx={styles.btn_appbar_search_icon}>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </AppBar>
      </Box>

      <Box
        sx={{
          ...styles.view_page_container,
          width: "100%",
          "& .MuiDataGrid-root": { width: "100% !important" },
          "& .MuiDataGrid-main": { width: "100% !important" },
          "& .MuiDataGrid-virtualScroller": { width: "100% !important" },
          "& .MuiDataGrid-columnHeaders": { width: "100% !important" },
          "& .MuiDataGrid-footerContainer": { width: "100% !important" },
        }}
      >
        <ViewDataGrid
          columns={itemGridData.columns}
          rows={preparedRows}
          menuItems={menuItems}
          tabletitle={itemGridData.name}
          gridid="itemcode"
        />
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default AddVariantPage;